import { useEffect, useMemo, useState } from 'react'

import { Grid } from '@mantine/core'

import SMSAnalyticsHeroStrip from './SMSAnalyticsHeroStrip'
import SMSVolumeContainer from './SMSVolumeContainer'
import SMSActivityByEmployeeContainer from './SMSActivityByEmployeeContainer'
import SMSFiltersBar from './SMSFiltersBar'
import { FiltersPortal } from '../CallAnalyticsPage/components/FiltersPortal'
import {
  activityAnalyticsDateRangeNeedsUrlSync,
  getActivityAnalyticsUrlDateRange,
} from '../CallAnalyticsPage/activityAnalyticsDateRange'
import {
  serializeDateRangeForUrl,
  updateUrl,
} from '../CallAnalyticsPage/utils'

export interface SMSAnalyticsTabProps {
  /**
   * Bumped by the page-level Refresh button. Threaded through to
   * containers (e.g. SMSVolumeContainer) that own their own useQuery so
   * they can call refetch() without us re-keying the whole tab — which
   * would reset local state like the SMS Volume bucket toggle.
   */
  refreshKey: number
}

const SMSAnalyticsTab = ({ refreshKey }: SMSAnalyticsTabProps) => {
  const [startDate, setStartDate] = useState(
    () => getActivityAnalyticsUrlDateRange().defaultStartDate
  )
  const [endDate, setEndDate] = useState(
    () => getActivityAnalyticsUrlDateRange().defaultEndDate
  )

  useEffect(() => {
    if (activityAnalyticsDateRangeNeedsUrlSync(startDate, endDate)) {
      updateUrl('dateRange', serializeDateRangeForUrl(startDate, endDate))
    }
  }, [startDate, endDate])

  const filters = useMemo(
    () => ({
      dateRange: { gte: startDate, lte: endDate },
    }),
    [startDate, endDate]
  )

  return (
    <>
      <FiltersPortal>
        <SMSFiltersBar
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </FiltersPortal>
      <SMSAnalyticsHeroStrip filters={filters} refreshKey={refreshKey} />
      <Grid gutter="xs">
        <Grid.Col span={12}>
          <SMSVolumeContainer filters={filters} refreshKey={refreshKey} />
        </Grid.Col>
        <Grid.Col span={12}>
          <SMSActivityByEmployeeContainer
            filters={filters}
            refreshKey={refreshKey}
          />
        </Grid.Col>
      </Grid>
    </>
  )
}

export default SMSAnalyticsTab
